import { useState, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { callEdgeFunction } from '@/lib/edge-functions'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import type { Order, FulfillmentStatus, FulfillmentType } from '@/types/database'

interface FulfillmentFormProps {
  order: Order
}

/**
 * Send shipping notification email to customer
 */
async function sendShippingNotification(orderId: string): Promise<boolean> {
  try {
    const result = await callEdgeFunction('send-shipping-notification', {
      method: 'POST',
      body: { order_id: orderId },
      withAuth: true,
    })

    if (result.error) {
      console.error('Failed to send shipping notification:', result.error)
      return false
    }

    return true
  } catch (error) {
    console.error('Error sending shipping notification:', error)
    return false
  }
}

export function FulfillmentForm({ order }: FulfillmentFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [fulfillmentType, setFulfillmentType] = useState<FulfillmentType | ''>(
    order.fulfillment_type || ''
  )
  const [fulfillmentStatus, setFulfillmentStatus] = useState<FulfillmentStatus>(
    order.fulfillment_status
  )
  const [trackingNumber, setTrackingNumber] = useState(order.tracking_number || '')
  const [trackingUrl, setTrackingUrl] = useState(order.tracking_url || '')
  const [manufacturerOrderId, setManufacturerOrderId] = useState(
    order.manufacturer_order_id || ''
  )
  const [adminNotes, setAdminNotes] = useState(order.admin_notes || '')

  // Track if shipping notification was already sent for this order
  const shippingEmailSentRef = useRef(order.fulfillment_status === 'shipped' || order.fulfillment_status === 'delivered')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const updates: Partial<Order> = {
        fulfillment_type: fulfillmentType || null,
        fulfillment_status: fulfillmentStatus,
        tracking_number: trackingNumber || null,
        tracking_url: trackingUrl || null,
        manufacturer_order_id: manufacturerOrderId || null,
        admin_notes: adminNotes || null,
      }

      // Set shipped_at if marking as shipped
      if (fulfillmentStatus === 'shipped' && !order.shipped_at) {
        updates.shipped_at = new Date().toISOString()
      }

      // Set delivered_at if marking as delivered
      if (fulfillmentStatus === 'delivered' && !order.delivered_at) {
        updates.delivered_at = new Date().toISOString()
      }

      const { error } = await (supabase
        .from('orders') as unknown as { update: (data: Partial<Order>) => { eq: (col: string, val: string) => Promise<{ error: Error | null }> } })
        .update(updates)
        .eq('id', order.id)

      if (error) throw error

      toast.success('Order updated successfully')

      // Send shipping notification email if status changed to shipped
      const wasNotShipped = order.fulfillment_status !== 'shipped' && order.fulfillment_status !== 'delivered'
      const isNowShipped = fulfillmentStatus === 'shipped'

      if (wasNotShipped && isNowShipped && !shippingEmailSentRef.current) {
        shippingEmailSentRef.current = true
        const emailSent = await sendShippingNotification(order.id)
        if (emailSent) {
          toast.success('Shipping notification email sent to customer')
        } else {
          toast.error('Failed to send shipping notification email')
        }
      }
    } catch (error) {
      console.error('Update error:', error)
      toast.error('Failed to update order')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="fulfillment_type">Fulfillment Type</Label>
          <Select
            value={fulfillmentType}
            onValueChange={(value) => setFulfillmentType(value as FulfillmentType)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="direct">Direct (Ship yourself)</SelectItem>
              <SelectItem value="manufacturer">Manufacturer (Drop-ship)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="fulfillment_status">Status</Label>
          <Select
            value={fulfillmentStatus}
            onValueChange={(value) => setFulfillmentStatus(value as FulfillmentStatus)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {fulfillmentType === 'manufacturer' && (
        <div className="space-y-2">
          <Label htmlFor="manufacturer_order_id">Manufacturer Order ID</Label>
          <Input
            id="manufacturer_order_id"
            value={manufacturerOrderId}
            onChange={(e) => setManufacturerOrderId(e.target.value)}
            placeholder="Order ID from manufacturer"
          />
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="tracking_number">Tracking Number</Label>
          <Input
            id="tracking_number"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            placeholder="Enter tracking number"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="tracking_url">Tracking URL</Label>
          <Input
            id="tracking_url"
            value={trackingUrl}
            onChange={(e) => setTrackingUrl(e.target.value)}
            placeholder="https://track.carrier.com/..."
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="admin_notes">Admin Notes</Label>
        <Textarea
          id="admin_notes"
          value={adminNotes}
          onChange={(e) => setAdminNotes(e.target.value)}
          placeholder="Internal notes about this order..."
          className="h-24"
        />
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Update Order'}
        </Button>

        {fulfillmentStatus === 'pending' && trackingNumber && (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setFulfillmentStatus('shipped')
            }}
          >
            Mark as Shipped
          </Button>
        )}
      </div>
    </form>
  )
}
